import {
  EXPLOSION_RADIUS,
  PLANE_GAME_LENGTH,
  PLANE_HEIGHT,
  PLANE_LENGTH,
  SOLDIER_RADIUS,
  teamColor,
  type Circle,
  type ShotMsg,
  type StateMsg,
} from './types'

const UNIT = PLANE_LENGTH / PLANE_GAME_LENGTH
const TRAIL_FADE_MS = 1200
const BLAST_MS = 700

interface ActiveShot {
  msg: ShotMsg
  startedAt: number
  finishedAt: number | null
  killed: Set<string>
}

export interface RenderOptions {
  showNext: boolean
}

export class Renderer {
  private readonly ctx: CanvasRenderingContext2D
  private readonly terrain: HTMLCanvasElement
  private readonly terrainCtx: CanvasRenderingContext2D

  private state: StateMsg | null = null
  private shot: ActiveShot | null = null
  private terrainKey = ''
  private localCraters: Circle[] = []
  private lastShots = new Map<string, ShotMsg>()
  private matchSeq = -1

  options: RenderOptions = { showNext: true }
  onShotFinished: (() => void) | null = null

  constructor(private readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('canvas 2d недоступен в этом браузере')
    this.ctx = ctx

    this.terrain = document.createElement('canvas')
    this.terrain.width = PLANE_LENGTH
    this.terrain.height = PLANE_HEIGHT
    const tctx = this.terrain.getContext('2d')
    if (!tctx) throw new Error('offscreen canvas 2d недоступен')
    this.terrainCtx = tctx

    requestAnimationFrame(this.frame)
  }

  setState(state: StateMsg): void {
    this.state = state
    if (state.matchSeq !== this.matchSeq) {
      this.matchSeq = state.matchSeq
      this.lastShots.clear()
    }
    const key = state.terrain
      ? `${state.matchSeq}:${state.terrain.explosions.length}`
      : ''
    if (key !== this.terrainKey) {
      this.terrainKey = key
      this.localCraters = []
      this.rebuildTerrain()
    }
  }

  playShot(msg: ShotMsg): void {
    this.shot = { msg, startedAt: performance.now(), finishedAt: null, killed: new Set() }
    this.lastShots.set(`${msg.player}:${msg.soldier}`, msg)
  }

  isAnimating(): boolean {
    return this.shot !== null && this.shot.finishedAt === null
  }

  deadDuringAnimation(playerIdx: number, soldierIdx: number): boolean {
    return this.shot?.killed.has(`${playerIdx}:${soldierIdx}`) ?? false
  }

  private rebuildTerrain(): void {
    const c = this.terrainCtx
    c.clearRect(0, 0, PLANE_LENGTH, PLANE_HEIGHT)
    const terrain = this.state?.terrain
    if (!terrain) return

    c.globalCompositeOperation = 'source-over'
    c.fillStyle = '#20303a'
    for (const circle of terrain.circles) {
      c.beginPath()
      c.arc(circle.x, circle.y, circle.r, 0, Math.PI * 2)
      c.fill()
    }

    c.globalCompositeOperation = 'destination-out'
    for (const hole of [...terrain.explosions, ...this.localCraters]) {
      c.beginPath()
      c.arc(hole.x, hole.y, hole.r, 0, Math.PI * 2)
      c.fill()
    }
    c.globalCompositeOperation = 'source-over'
  }

  private addCrater(x: number, y: number): void {
    this.localCraters.push({ x, y, r: EXPLOSION_RADIUS })
    this.rebuildTerrain()
  }

  private frame = (now: number): void => {
    this.resize()
    this.draw(now)
    requestAnimationFrame(this.frame)
  }

  private resize(): void {
    const dpr = window.devicePixelRatio || 1
    const rect = this.canvas.getBoundingClientRect()
    const w = Math.max(1, Math.round(rect.width * dpr))
    const h = Math.max(1, Math.round(rect.height * dpr))
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w
      this.canvas.height = h
    }
  }

  private draw(now: number): void {
    const ctx = this.ctx
    const scale = this.canvas.width / PLANE_LENGTH

    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    ctx.setTransform(scale, 0, 0, scale, 0, 0)

    this.drawPlane(ctx)
    ctx.drawImage(this.terrain, 0, 0)
    this.drawLastShot(ctx)
    this.drawSoldiers(ctx, now)
    this.drawShot(ctx, now)
  }

  private drawLastShot(ctx: CanvasRenderingContext2D): void {
    const state = this.state
    const turn = state?.turn
    if (!state || !turn || state.phase !== 'playing' || this.shot) return

    const ghost = this.lastShots.get(`${turn.player}:${turn.soldier}`)
    if (!ghost) return

    ctx.strokeStyle = teamColor(state.players[turn.player]?.team ?? 1)
    ctx.globalAlpha = 0.3
    ctx.lineWidth = 1.5
    ctx.lineJoin = 'round'
    ctx.setLineDash([6, 5])
    ctx.beginPath()
    ctx.moveTo(ghost.xs[0], ghost.ys[0])
    for (let i = 1; i < ghost.xs.length; i++) {
      ctx.lineTo(ghost.xs[i], ghost.ys[i])
    }
    ctx.stroke()

    ctx.setLineDash([])
    ctx.beginPath()
    ctx.arc(ghost.endX, ghost.endY, EXPLOSION_RADIUS, 0, Math.PI * 2)
    ctx.stroke()
    ctx.globalAlpha = 1
  }

  private drawPlane(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#f2efe4'
    ctx.fillRect(0, 0, PLANE_LENGTH, PLANE_HEIGHT)

    ctx.lineWidth = 1
    ctx.strokeStyle = 'rgba(32, 60, 48, 0.10)'
    ctx.beginPath()
    for (let gx = -PLANE_GAME_LENGTH / 2; gx <= PLANE_GAME_LENGTH / 2; gx++) {
      const x = Math.round(PLANE_LENGTH / 2 + gx * UNIT) + 0.5
      ctx.moveTo(x, 0)
      ctx.lineTo(x, PLANE_HEIGHT)
    }
    for (let gy = -15; gy <= 15; gy++) {
      const y = Math.round(PLANE_HEIGHT / 2 - gy * UNIT) + 0.5
      ctx.moveTo(0, y)
      ctx.lineTo(PLANE_LENGTH, y)
    }
    ctx.stroke()

    ctx.strokeStyle = 'rgba(32, 60, 48, 0.22)'
    ctx.beginPath()
    for (let gx = -25; gx <= 25; gx += 5) {
      const x = Math.round(PLANE_LENGTH / 2 + gx * UNIT) + 0.5
      ctx.moveTo(x, 0)
      ctx.lineTo(x, PLANE_HEIGHT)
    }
    for (let gy = -15; gy <= 15; gy += 5) {
      const y = Math.round(PLANE_HEIGHT / 2 - gy * UNIT) + 0.5
      ctx.moveTo(0, y)
      ctx.lineTo(PLANE_LENGTH, y)
    }
    ctx.stroke()

    ctx.strokeStyle = 'rgba(32, 60, 48, 0.55)'
    ctx.lineWidth = 1.5
    ctx.beginPath()
    ctx.moveTo(0, PLANE_HEIGHT / 2)
    ctx.lineTo(PLANE_LENGTH, PLANE_HEIGHT / 2)
    ctx.moveTo(PLANE_LENGTH / 2, 0)
    ctx.lineTo(PLANE_LENGTH / 2, PLANE_HEIGHT)
    ctx.stroke()
  }

  private nextSoldierIndex(playerIdx: number): number {
    const player = this.state?.players[playerIdx]
    if (!player?.soldiers) return -1
    for (let j = 1; j <= player.soldiers.length; j++) {
      const idx = (player.current + j) % player.soldiers.length
      if (player.soldiers[idx].alive && !this.deadDuringAnimation(playerIdx, idx)) return idx
    }
    return -1
  }

  private drawSoldiers(ctx: CanvasRenderingContext2D, now: number): void {
    const state = this.state
    if (!state) return

    const turn = state.turn
    state.players.forEach((player, pi) => {
      if (!player.soldiers) return
      player.soldiers.forEach((soldier, si) => {
        if (!soldier.alive || this.deadDuringAnimation(pi, si)) return

        const isCurrent = turn?.player === pi && turn.soldier === si
        const isNext = this.options.showNext && !isCurrent && this.nextSoldierIndex(pi) === si

        if (isCurrent) {
          const pulse = 0.5 + 0.5 * Math.sin(now / 260)
          ctx.strokeStyle = teamColor(player.team)
          ctx.globalAlpha = 0.35 + 0.45 * pulse
          ctx.lineWidth = 2
          ctx.beginPath()
          ctx.arc(soldier.x, soldier.y, SOLDIER_RADIUS + 5 + pulse * 3, 0, Math.PI * 2)
          ctx.stroke()
          ctx.globalAlpha = 1
        } else if (isNext) {
          ctx.strokeStyle = 'rgba(20, 34, 28, 0.55)'
          ctx.lineWidth = 1.5
          ctx.setLineDash([3, 3])
          ctx.beginPath()
          ctx.arc(soldier.x, soldier.y, SOLDIER_RADIUS + 4, 0, Math.PI * 2)
          ctx.stroke()
          ctx.setLineDash([])
        }

        ctx.fillStyle = teamColor(player.team)
        ctx.strokeStyle = '#12211b'
        ctx.lineWidth = 1.5
        ctx.beginPath()
        ctx.arc(soldier.x, soldier.y, SOLDIER_RADIUS, 0, Math.PI * 2)
        ctx.fill()
        ctx.stroke()

        if (!player.connected) {
          ctx.fillStyle = 'rgba(18, 33, 27, 0.55)'
          ctx.beginPath()
          ctx.arc(soldier.x, soldier.y, SOLDIER_RADIUS, 0, Math.PI * 2)
          ctx.fill()
        }

        this.drawLabel(ctx, player.name, soldier.x, soldier.y, isCurrent)
      })
    })
  }

  private drawLabel(
    ctx: CanvasRenderingContext2D,
    name: string,
    x: number,
    y: number,
    highlight: boolean,
  ): void {
    ctx.font = highlight ? 'bold 11px ui-monospace, monospace' : '10px ui-monospace, monospace'
    const w = ctx.measureText(name).width
    const bx = Math.min(Math.max(x - w / 2 - 3, 1), PLANE_LENGTH - w - 7)
    const by = y - SOLDIER_RADIUS - 16

    ctx.fillStyle = highlight ? 'rgba(18, 33, 27, 0.92)' : 'rgba(18, 33, 27, 0.6)'
    ctx.fillRect(bx, by, w + 6, 14)
    ctx.fillStyle = highlight ? '#f0d68a' : '#dfe6e0'
    ctx.fillText(name, bx + 3, by + 10.5)
  }

  private drawShot(ctx: CanvasRenderingContext2D, now: number): void {
    const shot = this.shot
    if (!shot) return

    const { msg } = shot
    const elapsed = now - shot.startedAt
    const total = msg.xs.length
    const progress = Math.min(total - 1, Math.floor((elapsed / 1000) * 1500))

    for (const hit of msg.hits) {
      if (hit.step <= progress) shot.killed.add(`${hit.player}:${hit.soldier}`)
    }

    let alpha = 1
    if (shot.finishedAt !== null) {
      const since = now - shot.finishedAt
      alpha = Math.max(0, 1 - since / TRAIL_FADE_MS)
      if (alpha === 0) {
        this.shot = null
        return
      }
    }

    const player = this.state?.players[msg.player]
    ctx.strokeStyle = teamColor(player?.team ?? 1)
    ctx.globalAlpha = alpha
    ctx.lineWidth = 2
    ctx.lineJoin = 'round'
    ctx.beginPath()
    ctx.moveTo(msg.xs[0], msg.ys[0])
    for (let i = 1; i <= progress; i++) {
      ctx.lineTo(msg.xs[i], msg.ys[i])
    }
    ctx.stroke()

    if (progress < total - 1) {
      ctx.fillStyle = '#f5e6a8'
      ctx.beginPath()
      ctx.arc(msg.xs[progress], msg.ys[progress], 3, 0, Math.PI * 2)
      ctx.fill()
    } else if (shot.finishedAt === null) {
      shot.finishedAt = now
      this.addCrater(msg.endX, msg.endY)
      this.onShotFinished?.()
    }

    if (shot.finishedAt !== null) {
      const blast = Math.min(1, (now - shot.finishedAt) / BLAST_MS)
      const radius = EXPLOSION_RADIUS * (0.6 + blast * 2.2)
      ctx.globalAlpha = alpha * (1 - blast)
      ctx.fillStyle = '#ffb347'
      ctx.beginPath()
      ctx.arc(msg.endX, msg.endY, radius, 0, Math.PI * 2)
      ctx.fill()
    }

    for (const hit of msg.hits) {
      if (hit.step > progress) continue
      const hitElapsed = ((progress - hit.step) / 1500) * 1000
      const t = Math.min(1, hitElapsed / BLAST_MS)
      const target = this.state?.players[hit.player]?.soldiers?.[hit.soldier]
      if (!target) continue
      ctx.globalAlpha = alpha * (1 - t)
      ctx.fillStyle = '#ff6b4a'
      ctx.beginPath()
      ctx.arc(target.x, target.y, SOLDIER_RADIUS * (1 + t * 2.5), 0, Math.PI * 2)
      ctx.fill()
    }

    ctx.globalAlpha = 1
  }
}
